import Big from "big.js";

// The server is authoritative for every amount (spec D5). These helpers exist
// so a form can show a grouped number and send back a plain decimal string —
// never a JavaScript number, which would round a rupiah away.
Big.DP = 6;
Big.RM = Big.roundHalfUp;

export function toBig(v: string | number | null | undefined): Big {
  if (v === null || v === undefined || v === "") return new Big(0);
  return new Big(String(v));
}

/** Decimal places every money amount is stored and shown with: every amount
 *  column in the schema is numeric(18,2). Read-only renderings of an amount
 *  pass this too, so a figure never reads differently from the field that
 *  edits it. */
export const MONEY_DP = 2;

/** Decimal places for a quantity (spec D5), the same precision QtyInput edits with. */
export const QTY_DP = 3;

/** Groups thousands with "." and uses "," for the decimal part. */
export function formatDecimal(v: string | null | undefined, dp = 0): string {
  if (v === null || v === undefined || v === "") return "";
  let b: Big;
  try {
    b = new Big(v);
  } catch {
    return String(v);
  }
  const fixed = b.toFixed(dp);
  const [whole, fraction] = fixed.split(".");
  const sign = whole.startsWith("-") ? "-" : "";
  const digits = sign ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return fraction ? `${sign}${grouped},${fraction}` : `${sign}${grouped}`;
}

/**
 * Reads what a user typed: "14.000" is fourteen thousand and "," is the
 * decimal separator, matching how the backend parses an uploaded sheet.
 * Returns null for a blank or unparseable entry so the caller can send null.
 */
export function parseDecimalInput(s: string): string | null {
  const raw = s.trim().replace(/\s/g, "");
  if (raw === "") return null;
  const normalized = raw.replace(/\./g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  try {
    return new Big(normalized).toString();
  } catch {
    return null;
  }
}

/**
 * At dp=0, toFixed never emits a "." — there is no fractional tail to trim,
 * only the integer itself, whose own trailing zeros are significant (560000
 * is not 56). The trim below only ever runs on the fractional part, after
 * confirming a decimal point is actually present; it stops at the "." on
 * its own (a "." is not a "0"), so it can never eat into the integer part
 * even when the whole fraction is zero (e.g. "560000.00" -> "560000").
 */
export function multiply(a: string, b: string, dp = 2): string {
  const fixed = toBig(a).times(toBig(b)).toFixed(dp);
  if (!fixed.includes(".")) return fixed;
  const trimmed = fixed.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed || "0";
}

/**
 * Rounds a decimal string to `dp` places, half up. The one place every
 * clamp-on-write and every preview calculation rounds through, so how many
 * digits a value actually carries never depends on which call site happened
 * to round it.
 */
export function roundTo(v: string, dp: number): string {
  return toBig(v).round(dp, Big.roundHalfUp).toString();
}

export function isPositiveDecimal(s: string | null | undefined): boolean {
  if (s === null || s === undefined || s === "") return false;
  try {
    return new Big(s).gt(0);
  } catch {
    return false;
  }
}

/**
 * Zero passes here but not `isPositiveDecimal` — a credit limit or a
 * quantity may legitimately be 0. Named apart from `isPositiveDecimal` on
 * purpose so the two can't be swapped for each other at a call site.
 */
export function isNonNegativeDecimal(s: string | null | undefined): boolean {
  if (s === null || s === undefined || s === "") return false;
  try {
    return new Big(s).gte(0);
  } catch {
    return false;
  }
}
